
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { UserIcon, HeartHandshake } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

const PortalButtons = () => {
  const { user, activeRole, getUserType } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('homepage');
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });

  const handleClientPortalClick = async (e) => {
    if (user) {
      e.preventDefault();
      
      if (activeRole === 'client') {
        navigate('/client-dashboard');
        return;
      }
      
      try {
        const userType = await getUserType();
        if (userType === 'client' || userType === 'unknown') {
          navigate('/client-dashboard');
        } else {
          navigate('/client-auth');
        }
      } catch (error) {
        console.error("Error determining user type:", error);
        navigate('/client-auth');
      }
    }
  };

  const handlePractitionerPortalClick = async (e) => {
    if (user) {
      e.preventDefault();
      
      if (activeRole === 'practitioner') {
        navigate('/practitioner-dashboard');
        return;
      }
      
      try {
        const userType = await getUserType();
        if (userType === 'practitioner') {
          navigate('/practitioner-dashboard');
        } else {
          navigate('/practitioner-auth');
        }
      } catch (error) {
        console.error("Error determining user type:", error);
        navigate('/practitioner-auth');
      }
    }
  };

  const CheckIcon = () => (
    <svg className="h-3 w-3 text-semantic-success" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
    </svg>
  );

  return (
    <section 
      ref={ref}
      className={`py-16 bg-brand-100/60 transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-brand text-warm-800 mb-4">
            {t('joinCommunity')}
          </h2>
          <p className="text-lg text-warm-600 max-w-2xl mx-auto">
            {t('joinCommunitySubtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          <Card 
            className={`bg-white/60 backdrop-blur-xl border border-white/80 shadow-xl card-hover transition-all duration-500 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ transitionDelay: '0ms' }}
          >
            <CardHeader className="text-center pb-2">
              <div className="mx-auto bg-brand-100 p-4 rounded-full w-16 h-16 flex items-center justify-center mb-4">
                <UserIcon className="h-8 w-8 text-brand-500" />
              </div>
              <CardTitle className="text-2xl font-display text-warm-800">{t('forClients')}</CardTitle>
              <CardDescription className="text-warm-600">{t('forClientsSubtitle')}</CardDescription>
            </CardHeader>
            <CardContent className="text-center pt-4">
              <ul className="space-y-2 text-warm-600 mb-6">
                <li className="flex items-start">
                  <span className="bg-semantic-success-light rounded-full p-1 mr-2 mt-0.5"><CheckIcon /></span>
                  {t('clientBenefits.verifiedPractitioners')}
                </li>
                <li className="flex items-start">
                  <span className="bg-semantic-success-light rounded-full p-1 mr-2 mt-0.5"><CheckIcon /></span>
                  {t('clientBenefits.easyBooking')}
                </li>
                <li className="flex items-start">
                  <span className="bg-semantic-success-light rounded-full p-1 mr-2 mt-0.5"><CheckIcon /></span>
                  {t('clientBenefits.virtualInPerson')}
                </li>
              </ul>
            </CardContent>
            <CardFooter className="pt-0">
              <Button 
                asChild 
                className="w-full bg-gradient-brand text-white hover:opacity-90 rounded-full font-semibold"
                onClick={handleClientPortalClick}
              >
                <Link to="/client-auth">{t('signupBookAppointment')}</Link>
              </Button>
            </CardFooter>
          </Card>

          <Card 
            className={`bg-white/60 backdrop-blur-xl border border-white/80 shadow-xl card-hover transition-all duration-500 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ transitionDelay: '150ms' }}
          >
            <CardHeader className="text-center pb-2">
              <div className="mx-auto bg-brand-100 p-4 rounded-full w-16 h-16 flex items-center justify-center mb-4">
                <HeartHandshake className="h-8 w-8 text-brand-500" />
              </div>
              <CardTitle className="text-2xl font-display text-warm-800">{t('forPractitioners')}</CardTitle>
              <CardDescription className="text-warm-600">{t('forPractitionersSubtitle')}</CardDescription>
            </CardHeader>
            <CardContent className="text-center pt-4">
              <ul className="space-y-2 text-warm-600 mb-6">
                <li className="flex items-start">
                  <span className="bg-brand-100 rounded-full p-1 mr-2 mt-0.5">
                    <svg className="h-3 w-3 text-brand-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                  </span>
                  {t('practitionerBenefits.professionalProfile')}
                </li>
                <li className="flex items-start">
                  <span className="bg-brand-100 rounded-full p-1 mr-2 mt-0.5">
                    <svg className="h-3 w-3 text-brand-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                  </span>
                  {t('practitionerBenefits.manageSchedule')}
                </li>
                <li className="flex items-start">
                  <span className="bg-brand-100 rounded-full p-1 mr-2 mt-0.5">
                    <svg className="h-3 w-3 text-brand-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                  </span>
                  {t('practitionerBenefits.globalDiscovery')}
                </li>
              </ul>
            </CardContent>
            <CardFooter className="pt-0">
              <Button 
                asChild 
                className="w-full bg-gradient-brand text-white hover:opacity-90 rounded-full font-semibold"
                onClick={handlePractitionerPortalClick}
              >
                <Link to="/practitioner-auth">{t('joinAsPractitioner')}</Link>
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default PortalButtons;
